import {
  CHECK_CATEGORY_ORDER,
  CHECK_CATEGORY_TITLES,
  categoryOf,
  type CheckCategory,
} from '../types/checks.js';
import type { MeasurementStatus } from '../types/common.js';
import type { CheckResult } from '../types/checks.js';
import type { ValidationReport } from '../types/result.js';

/**
 * The result model the interface renders: **groups → items**, nothing else.
 *
 * Design contract (v1.3):
 *  - a check becomes an item with a label, a value and the official range — no
 *    advice, no "what to do", no instructions;
 *  - the status vocabulary is exactly `pass | warning | fail | uncertain`;
 *    `not_checked` items are dropped from the groups (they are not findings) and
 *    are reported only as a counter;
 *  - a group's status is the worst status of its items, so the accordion header
 *    answers "does this part of the document pass?" in one look;
 *  - the official range is shown as data. An internal policy bound never replaces
 *    the official one: if the profile states 50–69 %, the item says 50–69 %.
 */

export type ValidationItemStatus = Extract<
  MeasurementStatus,
  'pass' | 'warning' | 'fail' | 'uncertain'
>;

export interface ValidationItem {
  id: string;
  label: string;
  status: ValidationItemStatus;
  /** Human-readable measured value, e.g. `60,0 %` or `0,8°`. */
  value?: string;
  /** Raw numeric value when the check measured one. */
  numericValue?: number;
  unit?: string;
  /** Official bounds of the document, when it states them. */
  allowed?: { min?: number; max?: number };
  /** Pre-formatted official range, e.g. `50–69 %`. */
  allowedText?: string;
  /** One short technical remark; never an instruction. */
  details?: string;
}

export interface ValidationGroup {
  id: CheckCategory;
  title: string;
  status: ValidationItemStatus;
  items: ValidationItem[];
  /** Items that could not be checked at all (no data, model missing). */
  notChecked: number;
}

const STATUS_RANK: Record<ValidationItemStatus, number> = {
  fail: 0,
  warning: 1,
  uncertain: 2,
  pass: 3,
};

const worstStatus = (statuses: ValidationItemStatus[]): ValidationItemStatus =>
  statuses.reduce<ValidationItemStatus>(
    (worst, status) => (STATUS_RANK[status] < STATUS_RANK[worst] ? status : worst),
    'pass',
  );

const formatNumber = (value: number, digits: number): string =>
  value.toFixed(digits).replace('.', ',');

const RATIO_UNITS = new Set(['ratio', '']);

/** Human value for one check, built from whatever the check measured. */
export const formatItemValue = (check: CheckResult): { value?: string; unit?: string } => {
  const display = check.details?.['displayValue'];
  if (typeof display === 'string' && display.length > 0) {
    return { value: display, ...(check.unit ? { unit: check.unit } : {}) };
  }
  if (typeof check.details?.['sharpnessScore'] === 'number') {
    return { value: formatNumber(check.details['sharpnessScore'] as number, 2) };
  }
  const raw = check.value ?? check.measurement?.value;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return {};
  const unit = check.unit ?? check.measurement?.unit ?? '';
  if (RATIO_UNITS.has(unit)) {
    return { value: `${formatNumber(raw * 100, 1)} %`, unit: '%' };
  }
  if (unit === 'deg') return { value: `${formatNumber(raw, 1)}°`, unit: 'deg' };
  if (unit === 'px') return { value: `${Math.round(raw)} px`, unit: 'px' };
  if (unit === 'bytes') return { value: `${Math.round(raw / 1024)} КБ`, unit: 'bytes' };
  return { value: formatNumber(raw, 2), ...(unit ? { unit } : {}) };
};

/** Official range exactly as the document states it (never the internal policy). */
export const formatAllowedRange = (check: CheckResult): string | undefined => {
  const official = check.official;
  const min = official?.min ?? check.min;
  const max = official?.max ?? check.max;
  if (min === undefined && max === undefined) return undefined;
  const unit = official?.unit ?? check.unit ?? '';
  const isRatio = RATIO_UNITS.has(unit);
  const asText = (value: number): string =>
    isRatio ? `${formatNumber(value * 100, 1)} %` : unit === 'deg' ? `${formatNumber(value, 1)}°` : formatNumber(value, 2);
  if (min !== undefined && max !== undefined) return `${asText(min)}–${asText(max)}`;
  if (min !== undefined) return `не менее ${asText(min)}`;
  if (max === undefined) return undefined;
  return `не более ${asText(max)}`;
};

const toItem = (check: CheckResult): ValidationItem => {
  const { value } = formatItemValue(check);
  const allowedText = formatAllowedRange(check);
  const official = check.official;
  const min = official?.min ?? check.min;
  const max = official?.max ?? check.max;
  const numericValue =
    typeof check.measurement?.value === 'number'
      ? check.measurement.value
      : typeof check.value === 'number'
        ? check.value
        : undefined;
  return {
    id: check.id,
    label: check.title,
    status: check.status as ValidationItemStatus,
    ...(value !== undefined ? { value } : {}),
    ...(numericValue !== undefined ? { numericValue } : {}),
    ...(check.unit ? { unit: check.unit } : {}),
    ...(min !== undefined || max !== undefined
      ? { allowed: { ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) } }
      : {}),
    ...(allowedText ? { allowedText } : {}),
    ...(typeof check.details?.['verdictReason'] === 'string'
      ? { details: check.details['verdictReason'] as string }
      : {}),
  };
};

/** Builds the accordion model: one group per checklist category, worst first order inside. */
export const buildValidationGroups = (report: ValidationReport | undefined): ValidationGroup[] => {
  if (!report) return [];
  const buckets = new Map<CheckCategory, CheckResult[]>();
  for (const check of report.checks) {
    if (check.status === 'not_checked') continue;
    const category = categoryOf(check.id);
    const bucket = buckets.get(category);
    if (bucket) bucket.push(check);
    else buckets.set(category, [check]);
  }
  return CHECK_CATEGORY_ORDER.filter((category) => buckets.has(category)).map((category) => {
    const checks = (buckets.get(category) ?? []).slice();
    checks.sort(
      (a, b) =>
        STATUS_RANK[a.status as ValidationItemStatus] -
          STATUS_RANK[b.status as ValidationItemStatus] ||
        a.title.localeCompare(b.title, 'ru'),
    );
    const items = checks.map(toItem);
    return {
      id: category,
      title: CHECK_CATEGORY_TITLES[category],
      status: worstStatus(items.map((item) => item.status)),
      items,
      notChecked: report.checks.filter(
        (check) => check.status === 'not_checked' && categoryOf(check.id) === category,
      ).length,
    };
  });
};
